import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import GoogleButtonLogin from "./GoogleButtonLogin";
import EmailLinkForm from "./EmailLinkForm";
import { capture } from "../../analytics/posthog";
import { useScrollLock } from "../../hooks/useScrollLock";
import { PRIVACY_PATH, TERMS_PATH } from "../legal/legal-config";

interface SignInModalProps {
  show: boolean;
  toggleModal: () => void;
  onLogin: () => void;
}

const SignInModal: React.FC<SignInModalProps> = ({
  show,
  toggleModal,
  onLogin,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Lock background scroll while open (iOS-safe, ref-counted).
  useScrollLock(show);

  // Fire the signup-started funnel event each time the modal opens.
  useEffect(() => {
    if (show) capture("signup_started", { source: "sign_in_modal" });
  }, [show]);

  // Close on Escape key
  useEffect(() => {
    if (!show) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") toggleModal();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, toggleModal]);

  // Move focus into the dialog when it opens. Otherwise focus stays on
  // whatever opened it — a button now behind a backdrop — and a keyboard or
  // screen-reader user lands nowhere. Close is the only control we own; the
  // Google button is a cross-origin frame and takes focus on its own.
  useEffect(() => {
    if (!show) return;
    const focusTimer = setTimeout(() => closeRef.current?.focus(), 40);
    return () => clearTimeout(focusTimer);
  }, [show]);

  if (!show) {
    return null;
  }

  // Use a portal so the modal renders directly on document.body,
  // escaping any ancestor position/overflow/transform that clips it.
  return ReactDOM.createPortal(
    <>
      {/* Inline keyframe – injected once */}
      <style>{`
        @keyframes bbModalIn {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);    }
        }
        @keyframes bbBackdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={toggleModal}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999999,
          backgroundColor: "rgba(0, 0, 0, 0.55)",
          animation: "bbBackdropIn 0.2s ease-out",
        }}
      />

      {/* Centered modal */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="bb-signin-title"
          onClick={(e) => e.stopPropagation()}
          style={{
            pointerEvents: "auto",
            position: "relative",
            width: "min(90vw, 400px)",
            background: "linear-gradient(160deg, #011e3a 0%, #012d55 100%)",
            border: "1px solid rgba(151, 197, 223, 0.13)",
            borderRadius: "16px",
            padding: "36px 28px 28px",
            boxShadow:
              "0 25px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(151,197,223,0.06) inset",
            animation: "bbModalIn 0.25s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          {/* Close button */}
          <button
            ref={closeRef}
            onClick={toggleModal}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              border: "none",
              background: "rgba(255,255,255,0.07)",
              cursor: "pointer",
              transition: "background 0.15s",
              color: "#fff",
              fontSize: 16,
              lineHeight: 1,
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.18)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.07)")
            }
          >
            ✕
          </button>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h3
              id="bb-signin-title"
              style={{
                margin: 0,
                fontSize: "1.5rem",
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "-0.01em",
              }}
            >
              Welcome
            </h3>
            <p
              style={{
                margin: "6px 0 0",
                fontSize: "0.875rem",
                color: "#97C5DF",
              }}
            >
              Sign in to continue
            </p>
          </div>

          {/* Divider */}
          <div
            style={{
              height: 1,
              background:
                "linear-gradient(90deg, transparent, rgba(151,197,223,0.18), transparent)",
              marginBottom: 24,
            }}
          />

          {/* Google sign-in button */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <GoogleButtonLogin onLogin={onLogin} />
          </div>

          {/* The door for people who won't or can't use Google. */}
          <EmailLinkForm source="sign_in_modal" />

          {/* Clickwrap consent. The links are real and open in a new tab so
              reading them doesn't abandon the sign-in; the API stamps the
              current Terms version on accounts created from here. */}
          <p
            style={{
              textAlign: "center",
              marginTop: 24,
              marginBottom: 0,
              fontSize: "0.72rem",
              lineHeight: 1.6,
              color: "#97C5DF",
              opacity: 0.72,
            }}
          >
            By continuing you agree to our{" "}
            <Link
              to={TERMS_PATH}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#bfe0f2", textDecoration: "underline" }}
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to={PRIVACY_PATH}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#bfe0f2", textDecoration: "underline" }}
            >
              Privacy Policy
            </Link>
            . You must be 21 or older.
          </p>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SignInModal;
